import { useCallback, useEffect, useMemo, useRef } from 'react';
import { validateField, setTouched, setFocused, setValue } from '@nsr/react-form';
import get from 'lodash/get';
import toPath from 'lodash/toPath';
import { useIntl } from 'react-intl';
import { batch, useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import useRendersCount from '@hedtech/userenderscount';
import { isPossiblePhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import PhoneNumber from '../PhoneNumber/PhoneNumber';

const splitCodeAndNumber = (value) => {
    if (typeof value === 'undefined' || !value) {
        return '';
    }
    // Skip parsing for very short values to improve performance
    if (typeof value === 'string' && value.length < 4) {
        return '';
    }
    try {
        const phoneNumber = parsePhoneNumber(value);
        if (phoneNumber) {
            return {
                code: phoneNumber.countryCallingCode,
                number: phoneNumber.nationalNumber
            };
        }
    } catch (_e) {
        // Ignore parsing errors for performance
    }
    return '';
};

const combineCodeAndNumber = (value, country) => {
    // Return the full international phone number for the PhoneInput component
    if (typeof value === 'object' && value !== null && value.code && value.number) {
        return `+${value.code}${value.number}`;
    }
    if (typeof value === 'string' && value) {
        if (value.startsWith('+')) {
            return value;
        }
        // Raw digit string (e.g. pre-populated from inquiry) - try to parse with country hint
        try {
            const phoneNumber = parsePhoneNumber(value, country || undefined);
            if (phoneNumber) {
                return phoneNumber.number;
            }
        } catch (_e) {
            // ignore
        }
        return value;
    }
    return '';
};
const FormPhoneField = props => {
    const {
        formatFromStore = combineCodeAndNumber,
        formatToStore = splitCodeAndNumber,
        helperText: helperTextProp = null,
        ignoreWhitespace = false,
        invalidFormatText = '',
        label = '',
        name: nameProp,
        onBlur: onBlurProp,
        onFocus: onFocusProp,
        required = false,
        requiredErrorTextId = 'validateRequired',
        validate: validateProp,
        country = '',
        ...rest
    } = props;
    useRendersCount(`FormPhoneField( ${nameProp} )`);
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const selectValue = useMemo(() => {
        return createSelector(
            state => get(state.form.values, toPath(nameProp)),
            value => value ? formatFromStore(value, country) : '',
        );
    }, [nameProp, formatFromStore, country]);
    const value = useSelector(selectValue);
    const validate = useMemo(() => {
        const isRequired = value => {
            if (!required) {
                return undefined;
            }
            value = ignoreWhitespace && value ? value.trim() : value;
            if (!value) {
                return formatMessage({ id: requiredErrorTextId });
            }
            // Only validate phone format if value looks complete (has reasonable length)
            if (value.length >= 8 && !isPossiblePhoneNumber(value)) {
                return invalidFormatText;
            }
        };
        const composeValidators = (fns = []) => (value, allValues, meta) => {
            const validators = [isRequired, ...fns];
            let error;
            for (let x = 0; x < validators.length; x++) {
                error = validators[x](value, allValues, meta);
                if (error) {
                    break;
                }
            }
            return error;
        };
        let validatorsArray = validateProp;
        if (validateProp && typeof validateProp === 'function') {
            validatorsArray = [validateProp];
        }
        return composeValidators(validatorsArray);
    }, [formatMessage, ignoreWhitespace, required, validateProp, requiredErrorTextId, invalidFormatText]);
    
    // Debounce validation to avoid expensive calls on every keystroke
    const validationTimeoutRef = useRef(null);
    useEffect(() => {
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
        }
        validationTimeoutRef.current = setTimeout(() => {
            dispatch(validateField({ name: nameProp, validate, value }));
        }, 300); // Validate after 300ms of no typing
        
        return () => {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
            }
        };
    }, [dispatch, nameProp, validate, value]);
    const onChange = useCallback(value => {
        dispatch(setValue({
            name: nameProp,
            value: formatToStore(value),
        }));
    }, [dispatch, formatToStore, nameProp]);
    const onBlur = useCallback(e => {
        batch(() => {
            dispatch(setTouched({ name: e.target.name, value: true }));
            dispatch(setFocused({ name: e.target.name, value: false }));
        });
        onBlurProp && onBlurProp(e);
    }, [dispatch, onBlurProp]);
    const onFocus = useCallback(e => {
        dispatch(setFocused({ name: e.target.name, value: true }));
        onFocusProp && onFocusProp(e);
    }, [dispatch, onFocusProp]);
    return (
        <PhoneNumber
            {...rest}
            helperText={helperTextProp}
            label={label}
            name={nameProp}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            required={required}
            value={value}
            country={country}
        />
    );
};
export default FormPhoneField;
